import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import {
  EventType,
  GetEventsDto,
  PublicEvent,
  WithPagination,
} from "@classview/shared-types";
import {
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

const getEvents = async (
  orgId: string,
  params: GetEventsDto,
): Promise<WithPagination<PublicEvent>> => {
  const response = await axiosClient.get(`/${orgId}/events`, {
    params,
  });
  return response.data;
};

export const getEventsQueryKey = (params: GetEventsDto, orgId?: string) => [
  "events",
  orgId ? `org-${orgId}` : "no-org",
  params.limit ? `limit-${params.limit}` : "no-limit",
  params.offset ? `offset-${params.offset}` : "no-offset",
  params.search ? `search-${params.search}` : "no-search",
  params.category ? `category-${params.category}` : "no-category",
  params.type ? `type-${params.type}` : "no-type",
  params.startDate ? `start-date-${params.startDate}` : "no-start-date",
  params.endDate ? `end-date-${params.endDate}` : "no-end-date",
  params.isAttending ? `is-attending-${params.isAttending}` : "no-is-attending",
];

export function useGetEvents(
  orgId: string,
  params: GetEventsDto,
  queryOptions?: UseQueryOptions<WithPagination<PublicEvent>, HttpError>,
) {
  return useQuery({
    queryKey: getEventsQueryKey(params, orgId),
    queryFn: () => getEvents(orgId, params),
    ...queryOptions,
  });
}

export function useInfiniteEvents({
  limit = 10,
  offset = 0,
  search,
  organisationId,
  startDate,
  endDate,
  isAttending,
  category,
  type,
}: {
  limit?: number;
  offset?: number;
  search?: string;
  organisationId: string;
  startDate?: string;
  endDate?: string;
  isAttending?: boolean;
  category?: string;
  type?: EventType;
}) {
  return useInfiniteQuery({
    queryKey: getEventsQueryKey(
      {
        limit,
        offset,
        search,
        startDate,
        endDate,
        isAttending,
        category,
        type,
      },
      organisationId,
    ),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      getEvents(organisationId, {
        limit,
        offset: pageParam * limit,
        search,
        startDate,
        endDate,
        isAttending,
        category,
        type,
      }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil(lastPage.count / limit);
      return lastPageParam < totalPages - 1 ? lastPageParam + 1 : undefined;
    },
  });
}
